import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { navigationService, userService } from 'src/config/rootService';
import { color, font } from 'src/shared/styles/variables';

import {
    EnterInsuranceInfoForm,
    InsuranceMemberData,
    InsurancePlanData,
} from '../components/EnterInsuranceInfoForm';
import { SubmissionModalContainer } from '../components/SubmissionModalContainer';
import { useSubmitInsuranceInfoMutation } from '../documents/SubmitInsuranceInfo.generated';

const FormBoundary = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 520,
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingBottom: '2.75rem',
});

const PageTitle = styled('h1')({ color: color.ebonyClay }, font.heavy(font.size.extraLarge));
const IntroText = styled('p')(
    {
        color: color.blueBayoux,
        marginBottom: 28,
    },
    font.light(font.size.medium),
);

export const EnterInsuranceInfoPage = () => {
    const [submitInsuranceInfoMutation, { data, error }] = useSubmitInsuranceInfoMutation();
    const [awaitingInsurance, setAwaitingInsurance] = useState(false);
    const [formCompanyUuid, setFormCompanyUuid] = useState('');
    const { t } = useTranslation();

    const asyncGetCompanyUuid = async () => {
        try {
            const {
                userDetails: { companyUuid },
            } = await userService.fetchUserInfo();
            setFormCompanyUuid(companyUuid);
        } catch (err) {
            err.name = 'Error retrieving company uuid';
            Sentry.captureException(err);
            navigationService.replace('/dashboard');
        }
    };
    const onFormSubmit = async (submission: InsurancePlanData & InsuranceMemberData) => {
        const {
            firstName = '',
            lastName = '',
            gender = '',
            birthDate = '',
            addressLine1 = '',
            addressLine2 = '',
            addressCity = '',
            addressState = '',
            addressZip = '',
            subscriberId = '',
            policyNumber = '',
        } = submission;
        const memberInput = {
            firstName,
            lastName,
            gender,
            dateOfBirth: birthDate,
            address: `${addressLine1} ${addressLine2}`.trim(),
            city: addressCity,
            state: addressState,
            postalCode: addressZip,
        };
        const planInput = {
            insurance: {
                policyMemberId: subscriberId,
                relationshipCode: '18',
                primaryFirstName: firstName,
                primaryLastName: lastName,
            },
            employerInsurance: {
                policyNumber,
                companyUuid: formCompanyUuid,
            },
        };

        try {
            setAwaitingInsurance(true);
            await submitInsuranceInfoMutation({ variables: { memberInput, planInput } });

            if (error) {
                // TODO: Display error feedback to user
                // eslint-disable-next-line no-console
                console.error(error);
                Sentry.withScope(scope => {
                    scope.setExtras({
                        error_info: 'Failed submitting member insurance plan info',
                        insurance: planInput.insurance,
                        employerInsurance: planInput.employerInsurance,
                        member: memberInput,
                    });
                    Sentry.captureException(error);
                });
                setAwaitingInsurance(false);
            } else {
                if (
                    data &&
                    data.createEligibilityCheck &&
                    data.createEligibilityCheck.eligibilityCheck &&
                    data.createEligibilityCheck.eligibilityCheck.isEligible
                ) {
                    navigationService.navigate('/insurance-approved');
                } else {
                    navigationService.navigate('/youre-on-your-way', {
                        title: t('insuranceForm:insurance_denied.page_title'),
                        description: t('insuranceForm:insurance_denied.page_description'),
                        componentKey: 'contact_us',
                    });
                }
            }
        } catch (err) {
            // TODO: Display error feedback to user
            // eslint-disable-next-line no-console
            console.error(err);
            Sentry.withScope(scope => {
                scope.setExtras({
                    error_info: 'Failed submitting member insurance plan info',
                    insurance: planInput.insurance,
                    employerInsurance: planInput.employerInsurance,
                    member: memberInput,
                });
                Sentry.captureException(err);
            });
            setAwaitingInsurance(false);
        }
    };

    useEffect(() => {
        asyncGetCompanyUuid();
    }, []);

    return (
        <FormBoundary>
            <PageTitle>{t('insuranceForm:insurance_form.page_title')}</PageTitle>
            <IntroText>{t('insuranceForm:insurance_form.introductory_text')}</IntroText>
            <EnterInsuranceInfoForm onFormSubmit={onFormSubmit} onCancel="/dashboard" />
            {awaitingInsurance && <SubmissionModalContainer />}
        </FormBoundary>
    );
};
